// Recurrence management library
// Functions for creating, updating, and managing recurrence rules for templates
// CHANGE: Created for Phase 2 - Task 3: Recurrence configuration
// CHANGE: Added calculateNextOccurrences function for real-time preview
// CHANGE: Added getRecurrenceLabel and getRecurrencePattern for improved UX display
// CHANGE: Removed all debug console statements for production

import { supabase } from './supabase';
import type { RecurrenceRule } from './supabase';

// Type for creating a new recurrence rule
export interface CreateRecurrenceData {
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
  days_of_week?: number[];
  day_of_month?: number | null;
  week_of_month?: number | null;
  interval?: number;
  month_of_year?: number | null;
}

// Get recurrence rule for a template
export async function getRecurrence(templateId: string): Promise<RecurrenceRule | null> {
  try {
    const { data, error } = await supabase
      .from('recurrence_rules')
      .select('*')
      .eq('template_id', templateId)
      .maybeSingle();

    if (error) {
      throw error;
    }

    return data;
  } catch (error) {
    throw error;
  }
}

// Save recurrence rule for a template (create or update)
export async function saveRecurrence(
  templateId: string,
  data: CreateRecurrenceData
): Promise<RecurrenceRule> {
  try {
    // Check if a rule already exists
    const existing = await getRecurrence(templateId);

    const ruleData = {
      template_id: templateId,
      frequency: data.frequency,
      days_of_week: data.days_of_week || [],
      day_of_month: data.day_of_month || null,
      week_of_month: data.week_of_month || null,
      interval: data.interval || 1,
      month_of_year: data.month_of_year || null
    };

    if (existing) {
      // Update existing rule
      const { data: rule, error } = await supabase
        .from('recurrence_rules')
        .update(ruleData)
        .eq('id', existing.id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return rule;
    } else {
      // Create new rule
      const { data: rule, error } = await supabase
        .from('recurrence_rules')
        .insert(ruleData)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return rule;
    }
  } catch (error) {
    throw error;
  }
}

// Delete recurrence rule for a template
export async function deleteRecurrence(templateId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('recurrence_rules')
      .delete()
      .eq('template_id', templateId);

    if (error) {
      throw error;
    }
  } catch (error) {
    throw error;
  }
}

// Format recurrence rule as human-readable text
export function formatRecurrenceText(rule: RecurrenceRule): string {
  const interval = rule.interval || 1;

  switch (rule.frequency) {
    case 'daily':
      if (interval === 1) {
        return 'Tous les jours';
      }
      return `Tous les ${interval} jours`;

    case 'weekly': {
      if (!rule.days_of_week || rule.days_of_week.length === 0) {
        // Simple weekly - no specific days
        if (interval === 1) {
          return 'Toutes les semaines';
        }
        return `Toutes les ${interval} semaines`;
      }

      // Custom weekly - specific days
      const dayNames = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
      const selectedDays = rule.days_of_week
        .sort((a, b) => a - b)
        .map(day => dayNames[day - 1])
        .join(', ');

      if (interval === 1) {
        return `Tous les ${selectedDays}`;
      }
      return `Toutes les ${interval} semaines le ${selectedDays}`;
    }

    case 'monthly': {
      const prefix = interval === 1 ? '' : `Tous les ${interval} mois `;

      if (rule.day_of_month) {
        const day = rule.day_of_month;
        let suffix = 'ème';
        if (day === 1) suffix = 'er';
        return `${prefix}Le ${day}${suffix} du mois`.trim();
      }

      if (rule.week_of_month && rule.days_of_week && rule.days_of_week.length > 0) {
        const weekNames = ['Premier', 'Deuxième', 'Troisième', 'Quatrième', 'Dernier'];
        const weekName = weekNames[rule.week_of_month - 1] || `Semaine ${rule.week_of_month}`;
        const dayNames = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
        const dayName = dayNames[rule.days_of_week[0] - 1];
        return `${prefix}${weekName} ${dayName} du mois`.trim();
      }

      return 'Mensuel (configuration incomplète)';
    }

    case 'yearly': {
      const monthNames = [
        'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
        'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
      ];

      const prefix = interval === 1 ? 'Chaque année' : `Tous les ${interval} ans`;

      if (!rule.month_of_year || rule.month_of_year < 1 || rule.month_of_year > 12) {
        return `${prefix} (mois non configuré)`;
      }

      const monthName = monthNames[rule.month_of_year - 1];

      if (rule.day_of_month) {
        const day = rule.day_of_month;
        let suffix = 'ème';
        if (day === 1) suffix = 'er';
        return `${prefix} le ${day}${suffix} ${monthName}`;
      }

      if (rule.week_of_month && rule.days_of_week && rule.days_of_week.length > 0) {
        const weekNames = ['premier', 'deuxième', 'troisième', 'quatrième', 'dernier'];
        const weekName = weekNames[rule.week_of_month - 1] || `semaine ${rule.week_of_month}`;
        const dayNames = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
        const dayName = dayNames[rule.days_of_week[0] - 1];
        return `${prefix} le ${weekName} ${dayName} de ${monthName}`;
      }

      return `${prefix} en ${monthName} (configuration incomplète)`;
    }

    default:
      return 'Récurrence inconnue';
  }
}

// Calculate next occurrence date (simplified version)
// Full implementation would be done in Phase 3 with proper date handling
export function calculateNextOccurrence(rule: RecurrenceRule): Date | null {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const interval = rule.interval || 1;

  switch (rule.frequency) {
    case 'daily':
      // Next occurrence is interval days away
      return new Date(today.getTime() + interval * 24 * 60 * 60 * 1000);

    case 'weekly': {
      if (!rule.days_of_week || rule.days_of_week.length === 0) {
        // Simple weekly - next occurrence is interval weeks away (same day of week)
        return new Date(today.getTime() + (interval * 7) * 24 * 60 * 60 * 1000);
      }

      // Custom weekly - specific days of week
      // Find next day of week
      const currentDay = today.getDay() === 0 ? 7 : today.getDay(); // Convert Sunday from 0 to 7
      const sortedDays = rule.days_of_week.sort((a, b) => a - b);

      // Find next day in the same week
      const nextDayInWeek = sortedDays.find(day => day > currentDay);

      if (nextDayInWeek) {
        const daysToAdd = nextDayInWeek - currentDay;
        return new Date(today.getTime() + daysToAdd * 24 * 60 * 60 * 1000);
      } else {
        // Next occurrence is in the next interval week(s)
        const firstDay = sortedDays[0];
        const daysToAdd = (7 * interval - currentDay) + firstDay;
        return new Date(today.getTime() + daysToAdd * 24 * 60 * 60 * 1000);
      }
    }

    case 'monthly': {
      if (rule.day_of_month) {
        const nextMonth = new Date(today);
        nextMonth.setMonth(today.getMonth() + interval);
        nextMonth.setDate(rule.day_of_month);
        return nextMonth;
      }

      // Week of month calculation would require more complex logic
      // Placeholder for Phase 3
      return null;
    }

    case 'yearly': {
      if (!rule.month_of_year || rule.month_of_year < 1 || rule.month_of_year > 12) {
        return null;
      }

      if (rule.day_of_month) {
        const nextYear = new Date(today);
        nextYear.setFullYear(today.getFullYear() + interval);
        nextYear.setMonth(rule.month_of_year - 1);
        nextYear.setDate(rule.day_of_month);
        return nextYear;
      }

      // Week of month calculation would require more complex logic
      return null;
    }

    default:
      return null;
  }
}

// Validate recurrence configuration
export function validateRecurrenceConfig(data: CreateRecurrenceData): string | null {
  // Validate interval
  const interval = data.interval || 1;
  if (interval < 1 || interval > 365) {
    return "L'intervalle doit être entre 1 et 365";
  }

  switch (data.frequency) {
    case 'daily':
      // No additional validation needed
      return null;

    case 'weekly':
      // Weekly can be either:
      // 1. Simple weekly (no days_of_week) - repeats every 7 days from start date
      // 2. Custom weekly (with days_of_week) - repeats on specific days
      if (data.days_of_week && data.days_of_week.length > 0) {
        // Check all days are between 1-7
        if (data.days_of_week.some(day => day < 1 || day > 7)) {
          return 'Jour de la semaine invalide (doit être entre 1 et 7)';
        }
      }
      return null;

    case 'monthly':
      // Must have either day_of_month OR (week_of_month AND days_of_week)
      const hasDayOfMonth = data.day_of_month !== null && data.day_of_month !== undefined;
      const hasWeekConfig = data.week_of_month && data.days_of_week && data.days_of_week.length > 0;

      if (!hasDayOfMonth && !hasWeekConfig) {
        return 'Veuillez configurer soit le jour du mois, soit la semaine du mois';
      }

      if (hasDayOfMonth) {
        if (data.day_of_month! < 1 || data.day_of_month! > 31) {
          return 'Le jour du mois doit être entre 1 et 31';
        }
      }

      if (hasWeekConfig) {
        if (data.week_of_month! < 1 || data.week_of_month! > 5) {
          return 'La semaine du mois doit être entre 1 et 5';
        }
        if (data.days_of_week!.length !== 1) {
          return 'Veuillez sélectionner un seul jour pour la récurrence par semaine';
        }
      }

      return null;

    case 'yearly':
      // Validate month_of_year
      if (!data.month_of_year || data.month_of_year < 1 || data.month_of_year > 12) {
        return "Le mois de l'année doit être entre 1 (janvier) et 12 (décembre)";
      }

      // Must have either day_of_month OR (week_of_month AND days_of_week)
      const hasYearlyDayOfMonth = data.day_of_month !== null && data.day_of_month !== undefined;
      const hasYearlyWeekConfig = data.week_of_month && data.days_of_week && data.days_of_week.length > 0;

      if (!hasYearlyDayOfMonth && !hasYearlyWeekConfig) {
        return "Veuillez configurer soit le jour du mois, soit la semaine du mois pour la récurrence annuelle";
      }

      if (hasYearlyDayOfMonth) {
        if (data.day_of_month! < 1 || data.day_of_month! > 31) {
          return 'Le jour du mois doit être entre 1 et 31';
        }
      }

      if (hasYearlyWeekConfig) {
        if (data.week_of_month! < 1 || data.week_of_month! > 5) {
          return 'La semaine du mois doit être entre 1 et 5';
        }
        if (data.days_of_week!.length !== 1) {
          return 'Veuillez sélectionner un seul jour pour la récurrence par semaine';
        }
      }

      return null;

    default:
      return 'Fréquence invalide';
  }
}

// Calculate N next occurrences based on recurrence configuration
// Returns array of dates in ISO format (YYYY-MM-DD)
export function calculateNextOccurrences(
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly',
  interval: number,
  daysOfWeek: number[],
  dayOfMonth: number | null,
  weekOfMonth: number | null,
  monthOfYear: number | null,
  startDate: Date,
  count: number = 3,
  endDate?: Date | null
): Date[] {
  const occurrences: Date[] = [];
  let currentDate = new Date(startDate);
  currentDate.setHours(0, 0, 0, 0);

  const maxIterations = count * 100; // Safety limit
  let iterations = 0;

  while (occurrences.length < count && iterations < maxIterations) {
    iterations++;

    let nextDate: Date | null = null;

    switch (frequency) {
      case 'daily':
        nextDate = new Date(currentDate);
        nextDate.setDate(currentDate.getDate() + interval);
        break;

      case 'weekly':
        if (!daysOfWeek || daysOfWeek.length === 0) {
          // Simple weekly - just add interval * 7 days (repeats on same day of week)
          nextDate = new Date(currentDate);
          nextDate.setDate(currentDate.getDate() + (interval * 7));
        } else {
          // Custom weekly - specific days of week
          const currentDayOfWeek = currentDate.getDay() === 0 ? 7 : currentDate.getDay();
          const sortedDays = [...daysOfWeek].sort((a, b) => a - b);

          // Find next day in same week
          const nextDayInWeek = sortedDays.find(day => day > currentDayOfWeek);

          if (nextDayInWeek) {
            nextDate = new Date(currentDate);
            nextDate.setDate(currentDate.getDate() + (nextDayInWeek - currentDayOfWeek));
          } else {
            // Jump to first day of next interval week
            const daysUntilNextWeek = (7 - currentDayOfWeek) + (interval - 1) * 7 + sortedDays[0];
            nextDate = new Date(currentDate);
            nextDate.setDate(currentDate.getDate() + daysUntilNextWeek);
          }
        }
        break;

      case 'monthly':
        if (dayOfMonth) {
          // Day of month mode (e.g., 15th of every month)
          nextDate = new Date(currentDate);
          nextDate.setMonth(currentDate.getMonth() + interval);
          nextDate.setDate(Math.min(dayOfMonth, getDaysInMonth(nextDate.getFullYear(), nextDate.getMonth())));
        } else if (weekOfMonth && daysOfWeek && daysOfWeek.length > 0) {
          // Week of month mode (e.g., 2nd Monday)
          nextDate = new Date(currentDate);
          nextDate.setMonth(currentDate.getMonth() + interval);
          nextDate = getNthWeekdayOfMonth(nextDate.getFullYear(), nextDate.getMonth(), weekOfMonth, daysOfWeek[0]);
        }
        break;

      case 'yearly':
        if (!monthOfYear || monthOfYear < 1 || monthOfYear > 12) {
          return occurrences;
        }

        if (dayOfMonth) {
          // Day of year mode (e.g., March 15th)
          nextDate = new Date(currentDate);
          nextDate.setFullYear(currentDate.getFullYear() + interval);
          nextDate.setMonth(monthOfYear - 1);
          nextDate.setDate(Math.min(dayOfMonth, getDaysInMonth(nextDate.getFullYear(), monthOfYear - 1)));
        } else if (weekOfMonth && daysOfWeek && daysOfWeek.length > 0) {
          // Week of year mode (e.g., 2nd Monday of March)
          nextDate = new Date(currentDate);
          nextDate.setFullYear(currentDate.getFullYear() + interval);
          nextDate = getNthWeekdayOfMonth(nextDate.getFullYear(), monthOfYear - 1, weekOfMonth, daysOfWeek[0]);
        }
        break;
    }

    if (!nextDate) {
      break;
    }

    // Check if we've exceeded end date
    if (endDate && nextDate > endDate) {
      break;
    }

    occurrences.push(nextDate);
    currentDate = nextDate;
  }

  return occurrences;
}

// Helper: Get number of days in a specific month
function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

// Helper: Get the Nth occurrence of a weekday in a month
// weekOfMonth: 1-4 for first through fourth, 5 for last
// dayOfWeek: 1 (Monday) through 7 (Sunday)
function getNthWeekdayOfMonth(year: number, month: number, weekOfMonth: number, dayOfWeek: number): Date | null {
  const firstDay = new Date(year, month, 1);
  const firstDayOfWeek = firstDay.getDay() === 0 ? 7 : firstDay.getDay();

  if (weekOfMonth === 5) {
    // Last occurrence
    const lastDay = new Date(year, month + 1, 0);
    const lastDayOfWeek = lastDay.getDay() === 0 ? 7 : lastDay.getDay();
    const daysBack = lastDayOfWeek >= dayOfWeek ? lastDayOfWeek - dayOfWeek : 7 - (dayOfWeek - lastDayOfWeek);
    return new Date(year, month, lastDay.getDate() - daysBack);
  }

  // Calculate first occurrence
  let daysToAdd = dayOfWeek - firstDayOfWeek;
  if (daysToAdd < 0) {
    daysToAdd += 7;
  }

  // Add weeks for nth occurrence (0 for first, 7 for second, etc.)
  daysToAdd += (weekOfMonth - 1) * 7;

  const targetDate = new Date(year, month, 1 + daysToAdd);

  // Verify we're still in the same month
  if (targetDate.getMonth() !== month) {
    return null;
  }

  return targetDate;
}

// Get simple frequency label (Quotidienne, Hebdomadaire, etc.)
export function getRecurrenceLabel(frequency: 'daily' | 'weekly' | 'monthly' | 'yearly'): string {
  switch (frequency) {
    case 'daily':
      return 'Quotidienne';
    case 'weekly':
      return 'Hebdomadaire';
    case 'monthly':
      return 'Mensuelle';
    case 'yearly':
      return 'Annuelle';
    default:
      return 'Récurrente';
  }
}

// Get human-readable recurrence pattern (detailed)
export function getRecurrencePattern(rule: RecurrenceRule): string | null {
  const interval = rule.interval || 1;

  switch (rule.frequency) {
    case 'daily':
      // No additional detail needed for daily - badge is enough
      return null;

    case 'weekly': {
      if (!rule.days_of_week || rule.days_of_week.length === 0) {
        // Simple weekly - no specific pattern to show
        return null;
      }

      // Custom weekly - show specific days
      const dayNames = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
      const selectedDays = rule.days_of_week
        .sort((a, b) => a - b)
        .map(day => dayNames[day - 1]);

      if (selectedDays.length === 1) {
        return `Chaque ${selectedDays[0]}`;
      } else if (selectedDays.length === 7) {
        return 'Tous les jours';
      } else {
        return selectedDays.map(day => day.charAt(0).toUpperCase() + day.slice(1)).join(', ');
      }
    }

    case 'monthly': {
      if (rule.day_of_month) {
        const day = rule.day_of_month;
        let suffix = '';
        if (day === 1) suffix = 'er';
        return `Le ${day}${suffix} de chaque mois`;
      }

      if (rule.week_of_month && rule.days_of_week && rule.days_of_week.length > 0) {
        const weekNames = ['premier', 'deuxième', 'troisième', 'quatrième', 'dernier'];
        const weekName = weekNames[rule.week_of_month - 1];
        const dayNames = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
        const dayName = dayNames[rule.days_of_week[0] - 1];
        return `Le ${weekName} ${dayName} du mois`;
      }

      return null;
    }

    case 'yearly': {
      const monthNames = [
        'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
        'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
      ];

      if (!rule.month_of_year || rule.month_of_year < 1 || rule.month_of_year > 12) {
        return null;
      }

      const monthName = monthNames[rule.month_of_year - 1];

      if (rule.day_of_month) {
        const day = rule.day_of_month;
        let suffix = '';
        if (day === 1) suffix = 'er';
        return `Le ${day}${suffix} ${monthName}`;
      }

      if (rule.week_of_month && rule.days_of_week && rule.days_of_week.length > 0) {
        const weekNames = ['premier', 'deuxième', 'troisième', 'quatrième', 'dernier'];
        const weekName = weekNames[rule.week_of_month - 1];
        const dayNames = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
        const dayName = dayNames[rule.days_of_week[0] - 1];
        return `Le ${weekName} ${dayName} de ${monthName}`;
      }

      return null;
    }

    default:
      return null;
  }
}
